// Flattens GET-shaped state (relation fields as nested {id,...} objects)
// back into the raw-id shape every POST/PUT expects, and always resends
// every field - mirrors exercise-templates/exerciseTemplatePayload.js.
// Necessary because every courses-hierarchy *Service.update() does a raw
// repository.save() of the request body with no prior load - any omitted
// field comes through as null and overwrites that column.

import { TOPIC_HOST_LEVEL_KEYS } from './courseLevels';

function idOf(value) {
    if (value === null || value === undefined) return null;
    if (typeof value === 'object') return value.id;
    return value;
}

// Course has no parent and (unlike every level below it) carries dAdd/dMod.
// CourseService.update() only ever sets dAdd server-side on create(), never
// on update() - but update() still raw-saves the whole object, so omitting
// dAdd here would null out the original creation date. Kept as its own
// builder rather than folded into buildHierarchyPayload for exactly this
// reason: no other level has this field at all.
export function buildCoursePayload(course, changes = {}) {
    const merged = { ...course, ...changes };
    return {
        id: merged.id,
        name: merged.name,
        description: merged.description || null,
        level: idOf(merged.level),
        classification: idOf(merged.classification),
        dAdd: merged.dAdd,
        dMod: merged.dMod,
    };
}

// Module/Chapter/Unit/CourseLesson all share this exact shape (a single
// fixed parent field). The parent id is always an explicit argument, never
// read off `node` itself - every parent FK is @ManyToOne(optional=false)
// on the backend, so a stale or missing value 500s (NOT NULL violation /
// NumberFormatException) rather than failing cleanly. Topic no longer uses
// this - see buildTopicPayload below, it has 5 possible parent fields
// instead of 1.
export function buildHierarchyPayload(level, parentId, node, changes = {}) {
    const merged = { ...node, ...changes };
    const payload = {
        id: merged.id,
        name: merged.name,
        description: merged.description || null,
        position: merged.position,
        // `visible` is a plain NOT NULL boolean on the backend with a
        // Java-side default of true - that default only applies to a
        // freshly-constructed object, so omitting this key here (as this
        // payload used to) silently reverted every "hidden" edit back to
        // visible on save, since the raw-resend PUT never carried the false
        // value through at all.
        visible: merged.visible !== false,
    };
    payload[level.parentField] = String(parentId);
    return payload;
}

// Topic can attach directly under any of course/module/chapter/unit/lesson
// now (exactly one set, backend 400s otherwise) - hostLevelKey/hostId are
// always explicit arguments (same discipline as buildHierarchyPayload's
// parentId), and every one of the 4 *other* parent fields is explicitly
// nulled rather than omitted, since PUT is a raw resend and "omitted" vs
// "null" must not be left to Jackson's default behavior to decide.
// Reparenting an existing Topic to a different host level is out of scope
// here - hostLevelKey/hostId are fixed at creation, only name/description
// change on edit (the caller always passes the topic's own current host).
export function buildTopicPayload(hostLevelKey, hostId, node, changes = {}) {
    const merged = { ...node, ...changes };
    const payload = {
        id: merged.id,
        name: merged.name,
        description: merged.description || null,
        position: merged.position,
        // see buildHierarchyPayload's comment above - same real bug, same fix.
        visible: merged.visible !== false,
    };
    TOPIC_HOST_LEVEL_KEYS.forEach((key) => {
        payload[key] = key === hostLevelKey ? String(hostId) : null;
    });
    return payload;
}

export function buildTopicElementPayload(topicId, block, changes = {}) {
    const merged = { ...block, ...changes };
    return {
        id: merged.id,
        topic: String(topicId),
        container: merged.container ? String(idOf(merged.container)) : null,
        type: merged.type,
        text: merged.text || null,
        file: merged.file ? String(idOf(merged.file)) : null,
        meta: merged.meta || null,
        config: merged.config || null,
        position: merged.position,
        blurEnabled: !!merged.blurEnabled,
        blurRequiresApproval: merged.blurRequiresApproval !== false,
    };
}

// No name/label field - a container is purely a visual-configuration
// object (layout + config), not a piece of content itself.
export function buildTopicContainerPayload(topicId, container, changes = {}) {
    const merged = { ...container, ...changes };
    return {
        id: merged.id,
        topic: String(topicId),
        layout: merged.layout,
        config: merged.config || null,
        position: merged.position,
    };
}

export function buildTopicExerciseTemplatePayload(topicId, link, changes = {}) {
    const merged = { ...link, ...changes };
    return {
        id: merged.id,
        topic: String(topicId),
        exerciseTemplate: String(idOf(merged.exerciseTemplate)),
        position: merged.position,
    };
}
